// Sign-in / sign-up screen (Google native + web, email, org waitlist) + the
// PWA install prompt. First screen an unauthenticated user sees.
import { useState, useEffect } from "react";
import { signInWithPopup, signInWithRedirect } from "firebase/auth";
import { doc, setDoc, collection, getDocs, query, orderBy } from "firebase/firestore";
import { auth, db, googleProvider } from "../lib/firebase";
import { PrabzoLogo, APP_VERSION } from "../ui/primitives";

export const Login=({authError="",onClearAuthError}={})=>{
  const [mode,setMode]=useState("main"); // main | email | org
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [appVersion,setAppVersion]=useState(APP_VERSION);
  const [apkUrl,setApkUrl]=useState("");
  const [installPrompt,setInstallPrompt]=useState(null);
  const [installed,setInstalled]=useState(false);
  const [showInstallGuide,setShowInstallGuide]=useState(false);
  const [testiIdx,setTestiIdx]=useState(0);
  useEffect(()=>{
    const t=setInterval(()=>setTestiIdx(i=>(i+1)%3),4000);
    return ()=>clearInterval(t);
  },[]);

  const isIOS=/iphone|ipad|ipod/i.test(navigator.userAgent);
  const isInStandaloneMode=window.matchMedia("(display-mode: standalone)").matches||window.navigator.standalone;
  useEffect(()=>{
    getDocs(query(collection(db,"app_versions"),orderBy("publishedAt","desc"))).then(s=>{
      if(!s.empty){setAppVersion(s.docs[0].data().version||null);setApkUrl(s.docs[0].data().url||"");}
    }).catch(()=>{});
    const handler=(e)=>{e.preventDefault();setInstallPrompt(e);};
    const onInstalled=()=>setInstalled(true);
    window.addEventListener("beforeinstallprompt",handler);
    window.addEventListener("appinstalled",onInstalled);
    return()=>{
      window.removeEventListener("beforeinstallprompt",handler);
      window.removeEventListener("appinstalled",onInstalled);
    };
  },[]);
  const handleInstall=async()=>{
    if(!installPrompt)return;
    installPrompt.prompt();
    const{outcome}=await installPrompt.userChoice;
    if(outcome==="accepted")setInstalled(true);
    setInstallPrompt(null);
  };
  const [isSignUp,setIsSignUp]=useState(false);
  const [consentChecked,setConsentChecked]=useState(false);
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [name,setName]=useState("");
  const [orgForm,setOrgForm]=useState({orgName:"",type:"",contactName:"",email:""});
  const [orgDone,setOrgDone]=useState(false);
  const isNative=window.Capacitor?.isNativePlatform?.();

  const signInGoogle=async()=>{
    setLoading(true);setErr("");
    try{
      if(isNative){
        const {FirebaseAuthentication}=await import("@capacitor-firebase/authentication");
        const result=await FirebaseAuthentication.signInWithGoogle();
        const {GoogleAuthProvider,signInWithCredential}=await import("firebase/auth");
        const credential=GoogleAuthProvider.credential(result.credential?.idToken);
        await signInWithCredential(auth,credential);
      } else {
        const isSafari=/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if(isSafari){
          await signInWithRedirect(auth,googleProvider);
        } else {
          await signInWithPopup(auth,googleProvider);
        }
      }
    }
    catch(e){
      const code=e?.code||"";
      const msg=(e?.message||"").toLowerCase();
      const userCancelled=["auth/popup-closed-by-user","auth/cancelled-popup-request","auth/user-cancelled"].includes(code)
        ||msg.includes("cancel")||msg.includes("closed");
      if(!userCancelled)setErr("Google sign-in failed. Try again.");
      setLoading(false);
    }
  };


  const handleEmail=async()=>{
    if(!email||!pass){setErr("Please fill in all fields.");return;}
    setLoading(true);setErr("");
    try{
      if(isSignUp){
        if(!name.trim()){setErr("Please enter your name.");setLoading(false);return;}
        if(!consentChecked){setErr("Please agree to the Privacy Policy to create an account.");setLoading(false);return;}
        const {createUserWithEmailAndPassword,updateProfile}=await import("firebase/auth");
        const {user:u}=await createUserWithEmailAndPassword(auth,email,pass);
        await updateProfile(u,{displayName:name});
        await u.reload(); // ensure auth.currentUser reflects the new displayName immediately,
                           // so the next screen (ProfileSetup) doesn't ask for the name again
      } else {
        const {signInWithEmailAndPassword}=await import("firebase/auth");
        await signInWithEmailAndPassword(auth,email,pass);
      }
    }catch(e){
      const msg={
        "auth/user-not-found":"No account found with this email.",
        "auth/wrong-password":"Incorrect password.",
        "auth/email-already-in-use":"An account already exists with this email.",
        "auth/weak-password":"Password must be at least 6 characters.",
        "auth/invalid-email":"Please enter a valid email address.",
        "auth/invalid-credential":"Incorrect email or password.",
      };
      setErr(msg[e.code]||"Something went wrong. Try again.");
      setLoading(false);
    }
  };

  const submitOrgWaitlist=async()=>{
    if(!orgForm.orgName||!orgForm.type||!orgForm.contactName||!orgForm.email){setErr("Please fill all fields.");return;}
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgForm.email)){setErr("Please enter a valid email address.");return;}
    setLoading(true);setErr("");
    try{
      await setDoc(doc(db,"org_waitlist",Date.now().toString()),{...orgForm,submittedAt:new Date().toISOString()});
      setOrgDone(true);
    }catch(e){setErr("Something went wrong. Try again.");}
    setLoading(false);
  };

  const GoogleIcon=()=>(
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );

  const BackBtn=({onClick})=>(
    <button onClick={onClick} className="flex items-center gap-1 text-sm text-gray-400 mb-4 hover:text-gray-600">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>Back
    </button>
  );

  const Header=()=>(
    <div className="text-center mb-4">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-2 shadow-lg" style={{background:"#0f766e",padding:"12px"}}>
        <PrabzoLogo/>
      </div>
      <h1 className="text-3xl font-black text-white mb-1" style={{fontFamily:"'Playfair Display',serif"}}>Prabzo</h1>
      <p className="text-teal-200 text-sm">Universal Freelancer Client Manager</p>
      <p className="text-teal-300 text-xs mt-1 opacity-80">700+ freelancers across 27 countries</p>
    </div>
  );

  return(
    <div className="min-h-screen flex items-start justify-center p-3" style={{background:"linear-gradient(135deg,#0f4c5c 0%,#0a7c8c 50%,#c8902a 100%)",paddingTop:`calc(env(safe-area-inset-top) + 1rem)`}}>
      <div className="w-full max-w-md">
        <Header/>
        <div id="recaptcha-container"/>

        {/* ── MAIN ── */}
        {mode==="main"&&(
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-800 mb-0" style={{fontFamily:"'Playfair Display',serif"}}>Welcome</h2>
            <p className="text-sm text-gray-400 mb-6">Sign in or create your account</p>
            {authError&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-4 flex items-center justify-between gap-2"><span>{authError}</span><button onClick={onClearAuthError} className="text-red-400 font-bold shrink-0">✕</button></p>}
            {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-4">{err}</p>}
            <div className="space-y-3">
              <button onClick={signInGoogle} disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all">
                <GoogleIcon/>{loading?"Signing in...":"Continue with Google"}
              </button>
              <div className="flex items-center gap-3"><div className="flex-1 h-px bg-gray-200"/><span className="text-xs text-gray-400 font-medium">or</span><div className="flex-1 h-px bg-gray-200"/></div>
              <button onClick={()=>{setMode("email");setErr("");}}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                Continue with Email
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              By continuing you agree to our{" "}
              <a href="/terms.html" target="_blank" className="underline text-gray-500">Terms</a>
              {" & "}
              <a href="/privacy.html" target="_blank" className="underline text-gray-500">Privacy Policy</a>

            </p>
          </div>
        )}


        {/* ── EMAIL ── */}
        {mode==="email"&&(
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <BackBtn onClick={()=>{setMode("main");setErr("");}}/>
            <h2 className="text-xl font-bold text-gray-800 mb-0" style={{fontFamily:"'Playfair Display',serif"}}>{isSignUp?"Create Account":"Sign In"}</h2>
            <p className="text-sm text-gray-400 mb-5">{isSignUp?"Join Prabzo today":"Welcome back"}</p>
            {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-4">{err}</p>}
            <div className="space-y-3">
              {isSignUp&&(
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Full Name</label>
                  <input className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200" placeholder="Your name" value={name} onChange={e=>setName(e.target.value)}/>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Email</label>
                <input type="email" className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)}/>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Password</label>
                <input type="password" className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200" placeholder="••••••••" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleEmail()}/>
              </div>
              {isSignUp&&(<label className="flex items-start gap-2 cursor-pointer mt-1"><input type="checkbox" checked={consentChecked} onChange={e=>setConsentChecked(e.target.checked)} className="mt-0.5 accent-teal-600 w-4 h-4 flex-shrink-0"/><span className="text-xs text-gray-500 leading-snug">I have read and agree to the <a href="/privacy.html" target="_blank" className="underline text-teal-600">Privacy Policy</a> and <a href="/terms.html" target="_blank" className="underline text-teal-600">Terms of Service</a>, and consent to processing of my personal data by Prabzo.</span></label>)}
              <button onClick={handleEmail} disabled={loading} className="w-full py-3 rounded-xl text-sm font-semibold text-white mt-1 transition-all" style={{background:"linear-gradient(135deg,#0f766e,#0a7c8c)"}}>
                {loading?"Please wait...":(isSignUp?"Create Account":"Sign In")}
              </button>
              <p className="text-center text-sm text-gray-500 pt-1">
                {isSignUp?"Already have an account? ":"Don't have an account? "}
                <button onClick={()=>{setIsSignUp(!isSignUp);setErr("");setConsentChecked(false);}} className="font-semibold text-teal-600">{isSignUp?"Sign In":"Sign Up"}</button>
              </p>
            </div>
          </div>
        )}

        {/* ── ORGANISATION ── */}
        {mode==="org"&&(
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <BackBtn onClick={()=>{setMode("main");setErr("");setOrgDone(false);}}/>
            {!orgDone?(
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0" style={{background:"#f0fdf4"}}>🏢</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800" style={{fontFamily:"'Playfair Display',serif"}}>Organisation Account</h2>
                    <p className="text-xs text-gray-400">Join the waitlist →</p>
                  </div>
                </div>
                <div className="rounded-2xl px-5 py-4 mb-5" style={{background:"linear-gradient(135deg,#fef9c3,#fef3c7)",border:"1px solid #fde68a"}}>
                  <p className="font-bold text-amber-800 text-sm">🚀 Launching Soon</p>
                  <p className="text-amber-700 text-xs mt-1 leading-relaxed">Be first in line — join the waitlist and we'll reach out personally.</p>
                </div>
                {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-4">{err}</p>}
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Organisation Name</label>
                    <input className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200"
                      placeholder="e.g. Fitness First Academy" value={orgForm.orgName} onChange={e=>setOrgForm(f=>({...f,orgName:e.target.value}))}/>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Type</label>
                    <select className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200 bg-white"
                      value={orgForm.type} onChange={e=>setOrgForm(f=>({...f,type:e.target.value}))}>
                      <option value="">Select type...</option>
                      {["Studio","Coaching Center","Gym","Dance Academy","Sports Club","Other"].map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Your Name</label>
                    <input className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200"
                      placeholder="Contact person name" value={orgForm.contactName} onChange={e=>setOrgForm(f=>({...f,contactName:e.target.value}))}/>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Email</label>
                    <input type="email" className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 border-gray-200"
                      placeholder="you@organisation.com" value={orgForm.email} onChange={e=>setOrgForm(f=>({...f,email:e.target.value}))}/>
                  </div>
                  <button onClick={submitOrgWaitlist} disabled={loading}
                    className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{background:"linear-gradient(135deg,#15803d,#16a34a)"}}>
                    {loading?"Submitting...":"Join Waitlist"}
                  </button>
                </div>
              </>
            ):(
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2" style={{fontFamily:"'Playfair Display',serif"}}>You're on the list!</h3>
                <p className="text-sm text-gray-500 mb-1">Thanks <strong>{orgForm.contactName}</strong>! We've added <strong>{orgForm.orgName}</strong> to the waitlist.</p>
                <p className="text-sm text-gray-400 mb-6">We'll email you at <strong>{orgForm.email}</strong> when Organisation accounts launch.</p>
                <button onClick={()=>{setMode("main");setOrgDone(false);setOrgForm({orgName:"",type:"",contactName:"",email:""});}}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 mx-auto">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-3 flex flex-col gap-3">
          {/* 1. Google Play badge — Android browser only */}
          {mode==="main"&&/android/i.test(navigator.userAgent)&&!isNative&&(
            <div className="flex justify-center">
              <a href="https://play.google.com/store/apps/details?id=com.shristudio.prabzo" target="_blank" rel="noopener noreferrer">
                <img src="/icons/google-play-badge.png" alt="Get it on Google Play" style={{height:"56px",width:"auto"}}/>
              </a>
            </div>
          )}
          {/* 2. Testimonial spinner */}
          {(()=>{
            const TESTIMONIALS=[
              {quote:"Managing 40+ students used to be chaos. Prabzo made it simple.",name:"Siyaram",role:"Tutor, Delhi"},
              {quote:"Finally I can send professional invoices and track payments without any hassle.",name:"Hamed A.",role:"Consultant, Muscat"},
              {quote:"The dashboard gives me a clear picture of my entire practice at a glance.",name:"Shyamol B.",role:"Professor, Gwalior"},
            ];
            return(
              <div className="w-full rounded-xl p-3" style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)"}}>
                <p className="text-xs italic text-center" style={{color:"rgba(255,255,255,0.9)",marginBottom:"4px",minHeight:"48px"}}>"{TESTIMONIALS[testiIdx].quote}"</p>
                <p className="text-xs text-center" style={{color:"rgba(255,255,255,0.5)"}}>— {TESTIMONIALS[testiIdx].name}, {TESTIMONIALS[testiIdx].role}</p>
                <div className="flex justify-center gap-1 mt-2">
                  {TESTIMONIALS.map((_,i)=>(
                    <div key={i} style={{width:i===testiIdx?"16px":"6px",height:"3px",borderRadius:"2px",background:i===testiIdx?"rgba(255,255,255,0.8)":"rgba(255,255,255,0.3)",transition:"all 0.3s"}}/>
                  ))}
                </div>
              </div>
            );
          })()}
          {/* 3. Organisation slim line */}
          {mode==="main"&&(
            <button onClick={()=>{setMode("org");setErr("");}}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/20 hover:border-white/40 transition-all text-sm"
              style={{background:"rgba(255,255,255,0.06)"}}>
              <div className="flex flex-col items-center gap-0.5">
                <span style={{color:"rgba(255,255,255,0.9)",fontWeight:"600",fontSize:"13px"}}>🏢 Running an organisation or studio?</span>
                <span style={{color:"rgba(255,255,255,0.5)",fontSize:"11px"}}>Join the waitlist — launching soon</span>
              </div>
            </button>
          )}
          {/* Platform-aware install buttons — iOS and desktop */}
          {(()=>{
            const isIOSBrowser=isIOS&&!isInStandaloneMode&&!isNative;
            if(isNative)return null;
            if(isIOSBrowser)return(
              <button onClick={()=>setShowInstallGuide(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/30 hover:border-white/50 hover:bg-white/10 transition-all">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v13M7 11l5 5 5-5"/><path d="M3 19h18"/>
                </svg>
                <span className="text-white text-xs font-semibold">Add to Home Screen</span>
              </button>
            );
            if(!isInStandaloneMode&&!installed)return installPrompt?(
              <button onClick={handleInstall}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/30 hover:border-white/50 hover:bg-white/10 transition-all">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v13M7 11l5 5 5-5"/><path d="M3 19h18"/>
                </svg>
                <span className="text-white text-xs font-semibold">Install App on This Device</span>
              </button>
            ):null;
            return null;
          })()}
          {installed&&<p className="text-teal-300 text-xs">✓ App installed successfully!</p>}
          {/* Install Guide Modal */}
          {showInstallGuide&&(
            <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50" onClick={()=>setShowInstallGuide(false)}>
              <div className="bg-white rounded-t-2xl p-6 w-full max-w-md" onClick={e=>e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-800 text-base">Add Prabzo to Home Screen</h3>
                  <button onClick={()=>setShowInstallGuide(false)} className="text-gray-400 text-xl">✕</button>
                </div>
                {isIOS?(
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">1</span><span>Tap the <strong>Share</strong> button at the bottom of Safari (the box with an arrow pointing up)</span></div>
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">2</span><span>Scroll down and tap <strong>"Add to Home Screen"</strong></span></div>
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">3</span><span>Tap <strong>"Add"</strong> in the top right corner</span></div>
                  </div>
                ):(
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">1</span><span>Tap the <strong>three dots menu (⋮)</strong> in Chrome's top right corner</span></div>
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">2</span><span>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></span></div>
                    <div className="flex items-start gap-3"><span className="bg-teal-100 text-teal-700 rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">3</span><span>Tap <strong>"Install"</strong> or <strong>"Add"</strong> to confirm</span></div>
                  </div>
                )}
                <button onClick={()=>setShowInstallGuide(false)} className="mt-5 w-full bg-teal-600 text-white py-3 rounded-xl font-semibold text-sm">Got it!</button>
              </div>
            </div>
          )}
          {/* 4. Footer links */}
          <a href="https://shri.life" target="_blank" className="text-teal-300 text-xs hover:text-teal-200 transition-colors" rel="noreferrer">Crafted with ❤️ by Shri Studio ↗</a>
          <p className="text-teal-400 text-xs opacity-60">© 2026 Prabzo {appVersion&&<span>{appVersion}</span>}</p>
        </div>
      </div>
    </div>
  );
};
